// src/common/components/serviceModalContent/ServiceModalContent.tsx
import React, { useState, useRef } from "react";
import classes from "./ServiceModalContent.module.scss";
import Button from "../button/Button";
import { useServiceModal } from "./useServiceModal";
import { useQr } from "../qr/hooks/useQr";
import {
  MdQrCodeScanner,
  MdCheckCircle,
  MdCancel,
  MdWifiOff,
} from "react-icons/md";
import BottomModal from "../bottomModal/BottomModal";
import { QrScanner } from "../qr/qrScanner/QrScanner";

type Props = {
  onClose: () => void;
};

export const ServiceModalContent = ({ onClose }: Props) => {
  const {
    code,
    stage,
    isOnline,
    disabled,
    handleQrScan,
    handleSubmit,
    handleCodeChange,
    setCode,
  } = useServiceModal(onClose);

  const { modalActive, setModalActive } = useQr(handleQrScan);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const handleDigitChange = (text: string, index: number) => {
    const newCode = code.split("");
    newCode[index] = text.replace(/\D/g, "");
    const updated = newCode.join("");
    setCode(updated);
    handleCodeChange(updated);

    if (text && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.title}>Обслуживание клиента</div>

      <div className={classes.body}>
        <div className={classes.codeContainer}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <input
              key={i}
              ref={(el) => {
                if (el) inputRefs.current[i] = el;
              }}
              className={classes.codeInput}
              value={code[i] || ""}
              onChange={(e) => handleDigitChange(e.target.value, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              type="text"
              maxLength={1}
              inputMode="numeric"
              disabled={stage !== "input"}
            />
          ))}
        </div>

        <div className={classes.qrRow}>
          <MdQrCodeScanner
            className={classes.qrIcon}
            size={82}
            onClick={() => setModalActive(true)}
          />
        </div>

        <div className={classes.statusArea}>
          {!isOnline && (
            <div className={classes.statusBanner} data-type="warning">
              <MdWifiOff size={24} />
              <span>Нет сети</span>
            </div>
          )}

          {stage === "loading" && (
            <div className={classes.statusBanner} data-type="info">
              <span>Проверяем код...</span>
            </div>
          )}

          {stage === "success" && (
            <div className={classes.statusBanner} data-type="success">
              <MdCheckCircle size={24} />
              <span>Переключены в режим обслуживания</span>
            </div>
          )}

          {stage === "error" && (
            <div className={classes.statusBanner} data-type="error">
              <MdCancel size={24} />
              <span>Код недействителен или уже использован</span>
            </div>
          )}
        </div>
      </div>

      <div className={classes.footer}>
        <Button
          title={!isOnline ? "Нет сети" : "Проверить код"}
          onClick={handleSubmit}
          disabled={disabled || stage !== "input"}
          loading={stage === "loading"}
          fullWidth
        />
        <button className={classes.cancelButton} onClick={onClose}>
          Отмена
        </button>
      </div>

      <BottomModal
        isVisible={modalActive}
        onClose={() => setModalActive(false)}
      >
        <div className={classes.qrModalContent}>
          <QrScanner onScan={handleQrScan} />
        </div>
      </BottomModal>
    </div>
  );
};